// src/components/reviews/ReviewForm.tsx
import React, { useState, useEffect } from 'react';
import { RatingStars } from './RatingStars';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import {ProductReview} from "../types"; // Импортируем стили

interface ReviewFormProps {
    productId: number;
    variantId?: number;
    review?: ProductReview | null;
    onSubmit: (data: any) => Promise<void>;
    onClose: () => void;
}

export const ReviewForm: React.FC<ReviewFormProps> = ({ productId, variantId, review, onSubmit, onClose }) => {
    const [rating, setRating] = useState(5);
    const [title, setTitle] = useState('');
    const [comment, setComment] = useState('');
    const [advantages, setAdvantages] = useState('');
    const [disadvantages, setDisadvantages] = useState('');
    const [loading, setLoading] = useState(false);

    const isEditing = !!review;

    useEffect(() => {
        if (review) {
            setRating(review.rating);
            setTitle(review.title || '');
            setComment(review.comment);
            setAdvantages(review.advantages || '');
            setDisadvantages(review.disadvantages || '');
        }
    }, [review]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!comment.trim()) {
            toast.error('Напишите отзыв');
            return;
        }

        if (comment.length < 10) {
            toast.error('Отзыв должен содержать минимум 10 символов');
            return;
        }

        setLoading(true);
        try {
            if (isEditing && review) {
                await onSubmit({
                    reviewId: review.id,
                    rating,
                    title: title.trim() || null,
                    comment: comment.trim(),
                    advantages: advantages.trim() || null,
                    disadvantages: disadvantages.trim() || null,
                });
                toast.success('Отзыв обновлен');
            } else {
                await onSubmit({
                    product_id: productId,
                    variant_id: variantId || null,
                    rating,
                    title: title.trim() || null,
                    comment: comment.trim(),
                    advantages: advantages.trim() || null,
                    disadvantages: disadvantages.trim() || null,
                    images: []
                });
                toast.success('Отзыв отправлен');
            }
            onClose();
        } catch (error: any) {
            console.error('Error submitting review:', error);
            toast.error(error.message || 'Ошибка при отправке отзыва');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="review-form-modal-overlay">
            <div className="review-form-container">
                <div className="review-form-header">
                    <h2 className="review-form-title">
                        {isEditing ? 'Редактировать отзыв' : 'Написать отзыв'}
                    </h2>
                    <button onClick={onClose} className="review-form-close">
                        <XMarkIcon style={{ width: 24, height: 24 }} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="review-form-rating">
                        <label className="review-form-rating-label">Оценка *</label>
                        <RatingStars rating={rating} size={28} interactive onRatingChange={setRating} />
                    </div>

                    <div className="review-form-group">
                        <label className="review-form-label">Заголовок (необязательно)</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Кратко опишите впечатление"
                            className="review-form-input"
                        />
                    </div>

                    <div className="review-form-group">
                        <label className="review-form-label">Отзыв *</label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Расскажите о своем опыте использования товара..."
                            rows={5}
                            className="review-form-textarea"
                        />
                        <div className="review-form-hint">
                            Минимум 10 символов
                        </div>
                    </div>

                    <div className="review-form-group">
                        <label className="review-form-label">Достоинства (необязательно)</label>
                        <textarea
                            value={advantages}
                            onChange={(e) => setAdvantages(e.target.value)}
                            placeholder="Что вам понравилось?"
                            rows={2}
                            className="review-form-textarea"
                        />
                    </div>

                    <div className="review-form-group">
                        <label className="review-form-label">Недостатки (необязательно)</label>
                        <textarea
                            value={disadvantages}
                            onChange={(e) => setDisadvantages(e.target.value)}
                            placeholder="Что можно улучшить?"
                            rows={2}
                            className="review-form-textarea"
                        />
                    </div>

                    <div className="review-form-actions">
                        <button
                            type="button"
                            onClick={onClose}
                            className="review-form-btn-cancel"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="review-form-btn-submit"
                        >
                            {loading ? 'Сохранение...' : (isEditing ? 'Сохранить изменения' : 'Отправить отзыв')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};